import React, { useRef, useEffect } from 'react';
import { X } from 'lucide-react';

// Siri-inspired morphing blob animation
const VoiceSphere = ({ isActive, isListening, isProcessing, isSpeaking, transcription, onClose }) => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const stateRef = useRef({ isListening, isProcessing, isSpeaking });
  const ampRef = useRef(0.08);

  // Keep stateRef always current so the animation loop doesn't need to re-init
  useEffect(() => {
    stateRef.current = { isListening, isProcessing, isSpeaking };
  }, [isListening, isProcessing, isSpeaking]);

  useEffect(() => {
    if (!isActive || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;
    const cx = W / 2;
    const cy = H / 2;
    const clipR = W / 2 - 1;   // circle clip radius
    const blobR = W * 0.27;    // base organic blob radius

    // Four Siri-style color blobs — blue, purple, cyan, rose
    const blobs = [
      { hue: 218, sat: 100, lit: 64, phase: 0,                 orbitPhase: 0,                speedMul: 1.0  },
      { hue: 275, sat: 88,  lit: 62, phase: Math.PI * 0.5,     orbitPhase: Math.PI * 0.5,    speedMul: 0.78 },
      { hue: 192, sat: 95,  lit: 58, phase: Math.PI * 1.0,     orbitPhase: Math.PI * 1.0,    speedMul: 0.92 },
      { hue: 342, sat: 95,  lit: 66, phase: Math.PI * 1.5,     orbitPhase: Math.PI * 1.5,    speedMul: 0.85 },
    ];

    /**
     * Draw one organic blob at (bx, by) with multi-harmonic distortion.
     * `amp` controls how much the shape morphs (0 = circle).
     */
    const drawBlob = (bx, by, r, hue, sat, lit, time, phase, amp) => {
      const pts = 96;
      ctx.beginPath();
      for (let i = 0; i <= pts; i++) {
        const a = (i / pts) * Math.PI * 2;
        const distortion =
          Math.sin(a * 2 + time * 1.15 + phase) * 0.38 +
          Math.sin(a * 3 - time * 0.68 + phase * 1.4) * 0.22 +
          Math.sin(a * 5 + time * 1.45 + phase * 0.55) * 0.13 +
          Math.sin(a * 7 - time * 0.92 + phase * 2.1) * 0.07;
        const radius = r * (1 + distortion * amp);
        const x = bx + Math.cos(a) * radius;
        const y = by + Math.sin(a) * radius;
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      ctx.closePath();

      // Radial gradient so each blob glows bright at centre, fades at edges
      const grad = ctx.createRadialGradient(bx - r * 0.12, by - r * 0.12, 0, bx, by, r * 1.55);
      grad.addColorStop(0,   `hsla(${hue}, ${sat}%, ${Math.min(lit + 18, 92)}%, 0.92)`);
      grad.addColorStop(0.45,`hsla(${hue}, ${sat}%, ${lit}%,        0.60)`);
      grad.addColorStop(1,   `hsla(${hue}, ${sat}%, ${lit - 12}%,   0)`);
      ctx.fillStyle = grad;
      ctx.fill();
    };

    const t0 = performance.now();

    const animate = () => {
      const elapsed = (performance.now() - t0) / 1000;
      const { isListening, isProcessing, isSpeaking } = stateRef.current;

      // Smooth amplitude & speed based on voice state
      const targetAmp   = isListening ? 0.52 : isSpeaking ? 0.68 : isProcessing ? 0.22 : 0.10;
      const speed       = isListening ? 1.35 : isSpeaking ? 1.75 : isProcessing ? 0.75 : 0.38;
      ampRef.current   += (targetAmp - ampRef.current) * 0.036;

      const amp = ampRef.current;
      const t   = elapsed * speed;

      ctx.clearRect(0, 0, W, H);

      // ── Dark background circle ──
      ctx.beginPath();
      ctx.arc(cx, cy, clipR + 1, 0, Math.PI * 2);
      ctx.fillStyle = '#0c0c18';
      ctx.fill();

      // ── Clip everything inside the circle ──
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, clipR, 0, Math.PI * 2);
      ctx.clip();

      // Screen blend mode makes overlapping blobs mix like light
      ctx.globalCompositeOperation = 'screen';

      blobs.forEach(b => {
        // Each blob orbits gently around the centre to avoid a static feel
        const orbitR = blobR * 0.22 * amp;
        const bx = cx + Math.cos(t * 0.28 * b.speedMul + b.orbitPhase) * orbitR;
        const by = cy + Math.sin(t * 0.28 * b.speedMul + b.orbitPhase) * orbitR;
        drawBlob(bx, by, blobR, b.hue, b.sat, b.lit, t * b.speedMul, b.phase, amp);
      });

      ctx.globalCompositeOperation = 'source-over';

      // Subtle centre highlight (depth cue)
      const highlight = ctx.createRadialGradient(cx, cy - blobR * 0.25, 0, cx, cy, blobR * 0.9);
      highlight.addColorStop(0, 'rgba(255,255,255,0.07)');
      highlight.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = highlight;
      ctx.fillRect(0, 0, W, H);

      // Edge vignette — softens the circle boundary
      const vignette = ctx.createRadialGradient(cx, cy, clipR * 0.55, cx, cy, clipR);
      vignette.addColorStop(0, 'rgba(0,0,0,0)');
      vignette.addColorStop(1, 'rgba(0,0,0,0.45)');
      ctx.fillStyle = vignette;
      ctx.fillRect(0, 0, W, H);

      ctx.restore();

      animRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [isActive]);

  const statusText = isListening  ? 'Listening...'
    : isProcessing ? 'Thinking...'
    : isSpeaking   ? 'Speaking...'
    : 'Ready...';

  return (
    <div className="flex flex-col items-center gap-5">
      {/* Canvas sphere */}
      <div className="relative">
        <button
          onClick={onClose}
          className="absolute -top-1 -right-1 z-10 w-7 h-7 rounded-full flex items-center justify-center text-primary/70 hover:text-primary transition-colors"
          style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(6px)' }}
          aria-label="Close voice mode"
        >
          <X size={14} />
        </button>

        <canvas
          ref={canvasRef}
          width={220}
          height={220}
          className="rounded-full"
          style={{
            boxShadow: '0 0 48px rgba(80, 100, 255, 0.35), 0 0 80px rgba(80, 100, 255, 0.15)',
          }}
        />
      </div>

      {/* Transcription bubble */}
      {transcription && (
        <div
          className="px-4 py-2.5 rounded-2xl max-w-xs text-primary text-center text-sm leading-relaxed"
          style={{
            background: 'rgba(255,255,255,0.10)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.12)',
          }}
        >
          &ldquo;{transcription}&rdquo;
        </div>
      )}

      {/* Status row */}
      <div className="flex items-center gap-2">
        {/* Animated dot */}
        <span
          className="inline-block w-1.5 h-1.5 rounded-full"
          style={{
            background: isListening ? '#5E9EFF'
              : isSpeaking           ? '#FF6B9C'
              : isProcessing         ? '#A78BFA'
              : 'rgba(255,255,255,0.4)',
            animation: (isListening || isSpeaking || isProcessing)
              ? 'pulse 1.1s ease-in-out infinite'
              : 'none',
          }}
        />
        <p className="text-primary/55 text-xs tracking-widest uppercase font-light">
          {statusText}
        </p>
      </div>
    </div>
  );
};

export default VoiceSphere;
